import type { FAQItem } from "@/components/marketing/FAQ";
import type { ProcessStep } from "@/components/marketing/ProcessSteps";
import type { CaseStudyCardData } from "@/components/marketing/CaseStudyCard";

export const CLIENT_NAMES: Array<string | { label: string; href?: string }> = [
  { label: "PURE Pickleball & Padel", href: "https://purepickleball.com" },
  "Xero Picks",
  "Open Sesame",
  "ASP Mesa",
  "ASP Queen Creek",
  "ASP Scottsdale",
  "First Class Lane",
  "New Era Drywall",
  "503 Construction",
  "JL Landscaping",
  "RCS Painting",
  "Shine Bright",
  "Patent Precheck",
  "Golden Grove Homebuyers",
  "Triple Seas Realty",
  "Zona Restoration",
  "Zona Roofing",
  "Legalhelp.ai",

  "True North",
  "Rythm HQ",
];

export const PROCESS: ProcessStep[] = [
  {
    idx: "01",
    title: "Audit",
    body: "We review your current Google and Meta accounts, pull spend and conversion data, and surface where attribution is broken and where spend is leaking.",
  },
  {
    idx: "02",
    title: "Build",
    body: "Campaign architecture, GA4 + dynamic call tracking, offline conversion pipeline, and creative brief, ready to launch in weeks, not quarters.",
  },
  {
    idx: "03",
    title: "Launch",
    body: "Phased rollout with conservative bids. We let the platform learn against clean data before scaling spend.",
  },
  {
    idx: "04",
    title: "Optimize",
    body: "Weekly cadence: search term reviews, creative refresh, bid signal tuning, and platform escalations via Partner support.",
  },
];

export const FAQS: FAQItem[] = [
  {
    q: "What types of businesses do you work with?",
    a: "We run paid media for service businesses, local brands, and growth-focused operators, from home services and trades to consumer goods, food & beverage, and sports & fitness. The common denominator is operators who measure on outcomes, not impressions.",
  },
  {
    q: "What advertising channels do you manage?",
    a: "Google Ads (Search, Performance Max, Display, Demand Gen, Video, Remarketing, Local Campaigns), Meta Ads (Facebook and Instagram), Local Services Ads, and YouTube, run together as a single integrated lead engine, not in isolation.",
  },
  {
    q: "What does engagement look like?",
    a: "It starts with a free audit of your existing Google and Meta accounts. If we're a fit, we move into a build phase (2–4 weeks) followed by a monthly retainer covering campaign management, tracking, optimization, and reporting.",
  },
  {
    q: "How do you track performance?",
    a: "We integrate your CRM with Google and Meta via offline conversion imports and CAPI. Bid algorithms learn from booked work and qualified leads, not raw form fills, and weekly reporting ties spend to revenue, not vanity metrics.",
  },
  {
    q: "Do you work with businesses outside Arizona?",
    a: "Yes. We run native EN / ES campaigns across the US. Our team is based in Scottsdale, AZ, and our accounts run nationwide.",
  },
  {
    q: "What size operator do you work with?",
    a: "We focus on operators spending $5K–$50K+/month on paid media. Below that, the audit cost typically outweighs the lift we can deliver.",
  },
  {
    q: "How do I know if my conversion tracking is actually working?",
    a: "Run Google Tag Assistant in preview mode on your live site and complete a real conversion (submit the form, click the call button) then confirm the tag reports as fired, not merely present. A tag can sit in an account for years, look perfectly installed, and never fire once. If your conversions dropped to zero on a specific date rather than declining over weeks, that is a tracking event, not a market one.",
  },
];

export const FEATURED_CASE_STUDIES: CaseStudyCardData[] = [
  {
    slug: "pool-franchise",
    industry: "Pool service",
    client: "Americas Swimming Pool Co.: franchise owner",
    headline: "Rebuilt attribution and clarified messaging for a multi-location franchise.",
    verified: false,
    channels: ["G", "M", "LSA"],
    methodology: {
      did: "Rebuilt the conversion pipeline across locations, then layered Search + LSA on top of a cleaner Meta creative test loop.",
      measured: "Booked-job revenue pushed back to Google and Meta via offline conversions, not raw form fills.",
    },
  },
  {
    slug: "roofing",
    industry: "Roofing",
    client: "Retail re-roof operator: Southwest",
    headline: "Geo-segmented Search and LSA program built around inspection-lead intent.",
    verified: false,
    channels: ["G", "LSA"],
    methodology: {
      did: "Restructured Search by service and ZIP, qualified for Google Guarantee, and stood up a weekly LSA dispute workflow.",
      measured: "Verified-call volume and recovered spend tracked monthly against the operator's CRM close data.",
    },
  },
  {
    slug: "landscaping",
    industry: "Landscaping",
    client: "Design-build landscaper",
    headline: "Account structure rebuilt to separate design-build from maintenance demand.",
    verified: false,
    channels: ["G", "M"],
    methodology: {
      did: "Split design-build and maintenance into separate campaign architectures with distinct creative and bid signals.",
      measured: "Lead-to-quote and quote-to-job rates tracked per campaign so spend follows the higher-margin work.",
    },
  },
];

export const TESTIMONIALS = [
  {
    body:
      "They listen first, diagnose what actually matters, and execute with consistency. Our online presence is stronger, our messaging is clearer, and our marketing is finally aligned with the experience we deliver.",
    name: "Richard Kent",
    role: "Franchise Owner",
    company: "Americas Swimming Pool Co.",
    verified: true,
  },
  {
    body:
      "Jett really knows his stuff. He's incredibly knowledgeable, proactive, and always on top of communication. We've been extremely happy with the work they've done to help us generate more leads, improve our marketing, and continue scaling our businesses.",
    name: "Aaron Chapin",
    role: "Owner",
    company: "Zona Restoration",
    verified: true,
  },
];
